import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DepartmentService, IDepartmentDetail } from '@shared/services/department.service';
import { IUser } from '@shared/services/user.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';

@Component({
	selector: 'app-create-update-department-dialog',
	templateUrl: './create-update-department.html',
	styleUrls: ['./create-update-department.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateUpdateDepartmentDialogComponent implements OnInit {
	@Output() saved = new EventEmitter<void>();
	form: FormGroup;
	department: IDepartmentDetail;
	httpError = '';
	isLoading = false;
	isEdit = false;
	availableUsers: IUser[] = [];

	constructor(
		@Inject(MAT_DIALOG_DATA)
		public data: {
			editDepartment: IDepartmentDetail;
			availableUsers: IUser[];
		},
		public dialogRef: MatDialogRef<CreateUpdateDepartmentDialogComponent>,
		private departmentService: DepartmentService,
		private fb: FormBuilder,
	) {
		this.department = {
			id: null,
			alias: null,
			name: null,
			shiftApprover: null,
			backupShiftApprover: null,
			shiftApprovers: null,
			isActive: true,
			employees: null,
		};

		if (data.editDepartment) {
			this.isEdit = true;
			this.department = { ...data.editDepartment };
		}
	}

	ngOnInit(): void {
		this.availableUsers = this.data.availableUsers.sort((a, b) => this.compare(a.name || '', b.name || '', true));
		this.initializeForm();
	}

	compare(a: any, b: any, isAsc: boolean) {
		return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
	}

	private initializeForm(): void {
		this.form = this.fb.group({
			alias: [this.department.alias, [Validators.required, Validators.maxLength(50), Validators.pattern(ValidatorPatterns.Alias), Validators.pattern(ValidatorPatterns.NoStartOrEndWhitespacePattern)]],
			name: [this.department.name, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Description)]],
			isActive: [this.department.isActive],
			shiftApprover: [this.department.shiftApprover, [Validators.required]],
			backupShiftApprover: [this.department.backupShiftApprover],
		});
		this.subscribeToFormChanges();
	}

	private subscribeToFormChanges(): void {
		this.form.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				if (key in this.department) {
					this.department[key] = value[key];
				}
			});
		});
	}

	get controls() {
		return this.form.controls;
	}

	getFirstValidationErrorName(formControlName: string) {
		return Object.keys(this.form.controls[formControlName].errors)[0];
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	async onSave() {
		this.httpError = '';
		if (this.isLoading) return;
		this.isLoading = true;

		const saveObservable = this.department.id ? this.departmentService.updateDepartment(this.department) : this.departmentService.createDepartment(this.department);

		saveObservable.subscribe({
			next: () => {
				this.saved.emit();
				this.dialogRef.close();
			},
			error: (errorResponse) => {
				this.httpError = `${errorResponse.status}`;
				this.isLoading = false;
			},
		});
	}
}
